/**
 * The `Pin` vocabulary: which way its arrow points, and how a travel time reads.
 *
 * Separate from `src/components/Pin.tsx` because `TravelWidget` (SYM-501) validates pin
 * config against these before it renders anything, and a `.tsx` file that exports both a
 * component and its schema cannot be fast-refreshed.
 */

/**
 * Which side of the label box the arrow sits on — legacy `PinDirections` in
 * `utils/signageTypes.ts`, whose values are config strings and therefore immovable.
 *
 * A `const` object rather than a TS `enum`: the values are what the config carries, and an
 * enum would additionally introduce a nominal type that a plain config string does not
 * satisfy without a cast.
 */
export const PinDirections = {
  Down: 'DOWN',
  Left: 'LEFT',
  Right: 'RIGHT',
  Up: 'UP',
} as const;

export type PinDirection = (typeof PinDirections)[keyof typeof PinDirections];

/**
 * Minutes -> `'1 hour 5 minutes'`, with the singular/plural of both units and a single space
 * only when both are present.
 *
 * Ported verbatim from the `travelTime` computed in `Pin.vue`, including the empty string for
 * a falsy input — a pin with no travel time renders its title alone, and `TravelWidget`
 * initialises every non-static pin to `0` before the first message (docs/MIGRATION-WIDGETS.md
 * §9), so the empty string *is* the loading state.
 *
 * The one addition is the guard against negative and non-finite values, which the legacy
 * expression would have rendered as `'-1 hours -5 minutes'`. No payload produces one.
 */
export function formatTravelTime(minutes: number | undefined): string {
  if (minutes === undefined || !Number.isFinite(minutes) || minutes <= 0) return '';

  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;

  let time = '';
  if (hours) time += hours === 1 ? '1 hour' : `${String(hours)} hours`;
  if (remainder) {
    if (time) time += ' ';
    time += remainder === 1 ? '1 minute' : `${String(remainder)} minutes`;
  }

  return time;
}
